import type { AlignmentEssayResponse, LatestAlignmentEssay } from './api';

// =============================================================================
// ALIGNMENT-ESSAY RECOVERY — W23's class, applied to the OTHER genuinely-long
// user-initiated route (docs/WALK-FINDINGS-2026-07-21.md; the owner's word
// `Go-longops`, 2026-07-24; audit table in docs/design/long-operation-honesty-
// 2026-07-24.md).
//
// THE SHAPE, and why it differs from Get Found's: POST /jobs/:id/alignment-
// essay runs the longest model cascade in the app — R385's ledger row names it
// as "the 4-call regen cascade" on Opus — but unlike POST /presence/generate
// it ALREADY PERSISTS: AlignmentEssayService writes its append-only
// alignment_essays row before the route ever answers. So the destroyed-work
// half of W23 does not apply here; only the LIE does. A client-side timeout on
// a run the server went on to finish showed "Couldn't write that essay",
// while the finished essay sat safely in the database.
//
// THE FIX is therefore the smaller, honest one: on a generate failure, re-read
// the artifact through the panel's OWN existing hydration endpoint (GET
// /jobs/:id/alignment-essay/latest — no new route, no new mechanism) and, if a
// row landed AT OR AFTER the attempt began, that attempt succeeded. Show the
// essay, and say plainly what happened to the connection.
//
// Pure + framework-free so the decision is directly unit-testable (this
// repo's vitest run has no jsdom — the same discipline presenceGenerateState.ts
// and driveExportState.ts follow).
// =============================================================================

/**
 * Did this essay row land as a result of the attempt that started at
 * `attemptStartedAt` (epoch ms)?
 *
 * Grounded in the ROW'S OWN createdAt, never a guess: if the newest
 * non-superseded essay is older than the attempt, the attempt genuinely
 * produced nothing and the failure is real. `null` (no essay at all) is
 * likewise a real failure, not a recovery.
 *
 * The comparison is inclusive of a small clock allowance because the two
 * timestamps come from different clocks — the browser's for the attempt, the
 * server's for the row. The allowance is deliberately SMALL (a few seconds):
 * it must never be wide enough to mistake a genuinely pre-existing essay for
 * a fresh one, which would show the user an OLD essay as if their failed
 * attempt had produced it — that would be inventing a result.
 */
export const RECOVERY_CLOCK_SKEW_MS = 5_000;

export function essayLandedAfter(
  essay: Pick<LatestAlignmentEssay, 'createdAt'> | null,
  attemptStartedAt: number,
): boolean {
  if (!essay) return false;
  const landed = Date.parse(essay.createdAt);
  if (Number.isNaN(landed)) return false; // unparseable = never claim recovery
  return landed >= attemptStartedAt - RECOVERY_CLOCK_SKEW_MS;
}

/** Normalize a hydration read into the SAME shape the generate path renders.
 *  lintGate/blocked/overBudget/grounding stay ABSENT — those are
 *  per-generation-run verdicts, and a recovered read never re-derives or
 *  replays a verdict it did not just compute (the GET route's own rule). */
export function recoveredEssayResult(essay: LatestAlignmentEssay): AlignmentEssayResponse {
  return {
    ok: true,
    essayId: essay.essayId,
    body: essay.body,
    wordCount: essay.wordCount,
    targetWords: essay.targetWords,
    maxWords: essay.maxWords,
    cultureSourceId: essay.cultureSourceId,
    cultureSourceUrl: essay.cultureSourceUrl,
    cultureSourceMissing: essay.cultureSourceMissing,
    cultureSourceProvenance: essay.cultureSourceProvenance,
    revised: false,
    staleFromCorrection: essay.staleFromCorrection,
  };
}

/** The line shown when a recovery succeeded. Every clause is a fact: the
 *  connection dropped (that is what the client saw), the writing finished
 *  server-side (that is what the row proves), and the essay below is that
 *  result. No stage is claimed, and nothing is promised about why the
 *  connection dropped. */
export function recoveredEssayNote(): string {
  return "Your connection dropped while I was writing — but the essay finished and was saved. Here it is.";
}
